/**
 * /admin/groups — manage groups and their access (polish task 04). A group
 * holds role grants like a user; members inherit them at resolve time. The
 * detail drawer adds/removes members (name/email typeahead) and grants the
 * whole group access via the jargon-free flow.
 */ import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogHeader, DialogTitle, Input, Label, Sheet, SheetContent, SheetHeader, SheetTitle, Table, TableBody, TableCell, TableHead, TableHeader, TableRow, Textarea } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Pencil, Trash2, UsersRound, X } from 'lucide-react';
import { useState } from 'react';
import { useApplyGroupTemplate, useListBundles, useListGroupTemplates, useListScopeNodes } from '../Access/hooks';
import { GrantDialog } from '../GrantDialog';
import { ResolvedId, useAdminNames } from '../nameResolution';
import { useListUsers, usePrograms } from '../People/hooks';
import { actionLabel, ViewModeBadge } from '../Permissions/PermissionsPage';
import { useAddGroupMember, useCreateGroup, useDeleteGroup, useGroupAssignments, useGroupMembers, useListGroups, useRemoveGroupMember, useUpdateGroup } from './hooks';
export function GroupsPage() {
    const [query, setQuery] = useState('');
    const [createOpen, setCreateOpen] = useState(false);
    const [fromTemplateOpen, setFromTemplateOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [detail, setDetail] = useState(null);
    const [grantGroup, setGrantGroup] = useState(null);
    const { data: groups, isLoading } = useListGroups(query);
    const del = useDeleteGroup();
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        "data-testid": Testids.adminGroups.page,
        children: [
            /*#__PURE__*/ _jsxs("header", {
                className: "flex items-center justify-between gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("h2", {
                                className: "text-xl font-semibold",
                                children: "Groups"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-muted-foreground",
                                children: "Put people in a group once, then grant the group access — members inherit it."
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex gap-2",
                        children: [
                            /*#__PURE__*/ _jsx(Button, {
                                variant: "outline",
                                "data-testid": Testids.adminGroups.fromTemplateButton,
                                onClick: ()=>setFromTemplateOpen(true),
                                children: "New team from template"
                            }),
                            /*#__PURE__*/ _jsxs(Button, {
                                "data-testid": Testids.adminGroups.createButton,
                                onClick: ()=>setCreateOpen(true),
                                children: [
                                    /*#__PURE__*/ _jsx(UsersRound, {
                                        className: "mr-2 h-4 w-4"
                                    }),
                                    "New group"
                                ]
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(Input, {
                placeholder: "Search groups",
                value: query,
                onChange: (e)=>setQuery(e.target.value),
                className: "max-w-sm"
            }),
            /*#__PURE__*/ _jsxs(Table, {
                "data-testid": Testids.adminGroups.table,
                children: [
                    /*#__PURE__*/ _jsx(TableHeader, {
                        children: /*#__PURE__*/ _jsxs(TableRow, {
                            children: [
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Name"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Key"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    children: "Members"
                                }),
                                /*#__PURE__*/ _jsx(TableHead, {
                                    className: "w-10"
                                })
                            ]
                        })
                    }),
                    /*#__PURE__*/ _jsxs(TableBody, {
                        children: [
                            (groups ?? []).map((g)=>/*#__PURE__*/ _jsxs(TableRow, {
                                    "data-testid": Testids.adminGroups.row,
                                    "data-row-id": g.id,
                                    className: "cursor-pointer",
                                    onClick: ()=>setDetail(g),
                                    children: [
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "font-medium",
                                            children: g.displayName
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            className: "text-muted-foreground",
                                            children: g.key
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            children: Number(g.memberCount ?? 0)
                                        }),
                                        /*#__PURE__*/ _jsx(TableCell, {
                                            onClick: (e)=>e.stopPropagation(),
                                            children: /*#__PURE__*/ _jsxs("div", {
                                                className: "flex justify-end",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Button, {
                                                        variant: "ghost",
                                                        size: "icon",
                                                        "data-testid": Testids.adminGroups.editButton,
                                                        onClick: ()=>setEditing(g),
                                                        children: /*#__PURE__*/ _jsx(Pencil, {
                                                            className: "h-4 w-4"
                                                        })
                                                    }),
                                                    /*#__PURE__*/ _jsx(Button, {
                                                        variant: "ghost",
                                                        size: "icon",
                                                        onClick: ()=>{
                                                            if (window.confirm(`Delete group "${g.displayName}"?`)) del.mutate({
                                                                id: g.id
                                                            });
                                                        },
                                                        children: /*#__PURE__*/ _jsx(Trash2, {
                                                            className: "h-4 w-4"
                                                        })
                                                    })
                                                ]
                                            })
                                        })
                                    ]
                                }, g.id)),
                            !isLoading && (groups ?? []).length === 0 && /*#__PURE__*/ _jsx(TableRow, {
                                children: /*#__PURE__*/ _jsx(TableCell, {
                                    colSpan: 4,
                                    className: "text-center text-muted-foreground",
                                    children: "No groups yet"
                                })
                            })
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(CreateGroupDialog, {
                open: createOpen,
                onOpenChange: setCreateOpen
            }),
            /*#__PURE__*/ _jsx(CreateFromTemplateDialog, {
                open: fromTemplateOpen,
                onOpenChange: setFromTemplateOpen
            }),
            editing && /*#__PURE__*/ _jsx(EditGroupDialog, {
                group: editing,
                open: !!editing,
                onOpenChange: (o)=>!o && setEditing(null)
            }),
            detail && /*#__PURE__*/ _jsx(GroupDetailDrawer, {
                group: detail,
                open: !!detail,
                onOpenChange: (o)=>!o && setDetail(null),
                onGrant: ()=>setGrantGroup(detail)
            }),
            grantGroup && /*#__PURE__*/ _jsx(GrantDialog, {
                open: !!grantGroup,
                onOpenChange: (o)=>!o && setGrantGroup(null),
                groupId: grantGroup.id,
                principalLabel: grantGroup.displayName
            })
        ]
    });
}
/**
 * Create-team-from-template (finalization WS2): pick a template, pick the
 * program its access applies to (the explicit scope — there is no default),
 * and apply. The team's roles materialize and the group is granted on that
 * program — but NOBODY gets access until members are added.
 */ function CreateFromTemplateDialog({ open, onOpenChange }) {
    const [templateId, setTemplateId] = useState('');
    const [scopeKind, setScopeKind] = useState('program');
    const [programQuery, setProgramQuery] = useState('');
    const [program, setProgram] = useState(null);
    const [orgQuery, setOrgQuery] = useState('');
    const [orgNode, setOrgNode] = useState(null);
    const [reason, setReason] = useState('');
    const { data: templates } = useListGroupTemplates('published');
    const { data: programs } = usePrograms(programQuery);
    // Org-unit scopes: the backend grants on ANY node — tenant root, sector,
    // business unit, portfolio — not just programs. Program nodes are excluded
    // here (they have their own mint-on-demand picker above), and project
    // nodes belong to zou surfaces.
    const scopeNodesQuery = useListScopeNodes(orgQuery, {
        enabled: scopeKind === 'org'
    });
    const orgNodes = (scopeNodesQuery.data ?? []).filter((n)=>n.kind !== 'program' && n.kind !== 'project');
    const apply = useApplyGroupTemplate();
    const selected = (templates ?? []).find((t)=>t.id === templateId);
    const scopePicked = scopeKind === 'program' ? !!program : !!orgNode;
    const canApply = !!selected && scopePicked && !apply.isPending;
    const reset = ()=>{
        setTemplateId('');
        setScopeKind('program');
        setProgramQuery('');
        setProgram(null);
        setOrgQuery('');
        setOrgNode(null);
        setReason('');
    };
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: (o)=>{
            if (!o) reset();
            onOpenChange(o);
        },
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            className: "max-h-[85vh] overflow-y-auto",
            "data-testid": Testids.adminGroups.templateDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "New team from template"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Team template"
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "grid gap-1.5",
                                    children: [
                                        (templates ?? []).map((t)=>/*#__PURE__*/ _jsxs("button", {
                                                type: "button",
                                                "data-testid": Testids.adminGroups.templateOption,
                                                "data-template-key": t.key,
                                                onClick: ()=>setTemplateId(t.id),
                                                className: `rounded-md border px-3 py-2 text-left text-sm transition-colors ${templateId === t.id ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40'}`,
                                                children: [
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "font-medium",
                                                        children: t.displayName
                                                    }),
                                                    t.description && /*#__PURE__*/ _jsx("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: t.description
                                                    }),
                                                    t.pinnedRoles.length > 0 && /*#__PURE__*/ _jsx("div", {
                                                        className: "mt-1 flex flex-wrap gap-1",
                                                        children: t.pinnedRoles.map((p)=>/*#__PURE__*/ _jsx("span", {
                                                                className: "rounded bg-muted px-1.5 py-0.5 text-[10px]",
                                                                title: `Will add the "${p.displayName || p.key}" role (v${p.version}) to your organization if it isn't there yet`,
                                                                children: p.displayName || p.key
                                                            }, `${p.key}:${p.version}`))
                                                    })
                                                ]
                                            }, t.id)),
                                        (templates ?? []).length === 0 && /*#__PURE__*/ _jsx("p", {
                                            className: "text-sm text-muted-foreground",
                                            children: "No team templates published yet."
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Where this team works"
                                }),
                                /*#__PURE__*/ _jsx("div", {
                                    className: "flex gap-1.5",
                                    "data-testid": Testids.adminGroups.templateScopeKind,
                                    children: [
                                        [
                                            'program',
                                            'A program'
                                        ],
                                        [
                                            'org',
                                            'An org unit or the whole organization'
                                        ]
                                    ].map(([kind, label])=>/*#__PURE__*/ _jsx("button", {
                                            type: "button",
                                            "data-scope-kind": kind,
                                            onClick: ()=>setScopeKind(kind),
                                            className: `rounded-md border px-2.5 py-1 text-xs transition-colors ${scopeKind === kind ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/40'}`,
                                            children: label
                                        }, kind))
                                }),
                                scopeKind === 'org' ? orgNode ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between rounded-md border px-3 py-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                orgNode.name,
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "ml-1.5 text-xs text-muted-foreground",
                                                    children: orgNode.kind
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "ghost",
                                            size: "sm",
                                            onClick: ()=>setOrgNode(null),
                                            children: "Change"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.adminGroups.templateScopeNodeSearch,
                                            placeholder: "Search org units — business units, sectors, portfolios, the organization",
                                            value: orgQuery,
                                            onChange: (e)=>setOrgQuery(e.target.value)
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "max-h-32 overflow-auto rounded-md border",
                                            children: [
                                                orgNodes.map((n)=>/*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        "data-testid": Testids.adminGroups.templateScopeNodeOption,
                                                        "data-node-id": n.id,
                                                        className: "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                                                        onClick: ()=>setOrgNode({
                                                                id: n.id,
                                                                name: n.displayName || n.id,
                                                                kind: n.kind
                                                            }),
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                children: n.displayName || n.id
                                                            }),
                                                            /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: n.kind
                                                            })
                                                        ]
                                                    }, n.id)),
                                                orgNodes.length === 0 && /*#__PURE__*/ _jsx("div", {
                                                    className: "px-3 py-2 text-sm text-muted-foreground",
                                                    children: "No org units found"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[11px] text-muted-foreground",
                                            children: "Access inherits downward — a team on a business unit covers every program beneath it."
                                        })
                                    ]
                                }) : program ? /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between rounded-md border px-3 py-2 text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsx("span", {
                                            children: program.name
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "ghost",
                                            size: "sm",
                                            onClick: ()=>setProgram(null),
                                            children: "Change"
                                        })
                                    ]
                                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Input, {
                                            "data-testid": Testids.adminGroups.templateProgramSearch,
                                            placeholder: "Search programs by name or contract number",
                                            value: programQuery,
                                            onChange: (e)=>setProgramQuery(e.target.value)
                                        }),
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "max-h-32 overflow-auto rounded-md border",
                                            children: [
                                                (programs ?? []).map((p)=>/*#__PURE__*/ _jsxs("button", {
                                                        type: "button",
                                                        "data-testid": Testids.adminGroups.templateProgramOption,
                                                        "data-program-id": p.id,
                                                        className: "block w-full px-3 py-2 text-left text-sm hover:bg-muted",
                                                        onClick: ()=>setProgram({
                                                                id: p.id,
                                                                name: p.programNameFull || p.contractNumber || p.id
                                                            }),
                                                        children: [
                                                            /*#__PURE__*/ _jsx("div", {
                                                                children: p.programNameFull || '(unnamed program)'
                                                            }),
                                                            p.contractNumber ? /*#__PURE__*/ _jsx("div", {
                                                                className: "text-xs text-muted-foreground",
                                                                children: p.contractNumber
                                                            }) : null
                                                        ]
                                                    }, p.id)),
                                                (programs ?? []).length === 0 && /*#__PURE__*/ _jsx("div", {
                                                    className: "px-3 py-2 text-sm text-muted-foreground",
                                                    children: "No programs found"
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "text-[11px] text-muted-foreground",
                                            children: "Required — the team's access applies only under this program."
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Reason (optional)"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    value: reason,
                                    placeholder: "Why this team exists (appears in the audit log)",
                                    onChange: (e)=>setReason(e.target.value)
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("p", {
                            className: "rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900",
                            children: [
                                "This team starts with ",
                                /*#__PURE__*/ _jsx("strong", {
                                    children: "no members"
                                }),
                                ". Nobody gains access until you add people to it."
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            "data-testid": Testids.adminGroups.templateApplyButton,
                            disabled: !canApply,
                            onClick: async ()=>{
                                if (!selected || !scopePicked) return;
                                try {
                                    await apply.mutateAsync({
                                        templateId: selected.id,
                                        ...scopeKind === 'org' && orgNode ? {
                                            scopeNodeId: orgNode.id
                                        } : {
                                            programId: program?.id ?? '',
                                            programName: program?.name ?? ''
                                        },
                                        reason: reason.trim()
                                    });
                                } catch  {
                                    return; // hook toasts the failure; keep the dialog open
                                }
                                reset();
                                onOpenChange(false);
                            },
                            children: "Create team"
                        })
                    ]
                })
            ]
        })
    });
}
function CreateGroupDialog({ open, onOpenChange }) {
    const [form, setForm] = useState({
        displayName: '',
        key: '',
        description: ''
    });
    const create = useCreateGroup();
    const valid = form.displayName && form.key;
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminGroups.createDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "New group"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Display name"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    value: form.displayName,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            displayName: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Key"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    placeholder: "lowercase-stable-id (e.g. delivery-leads)",
                                    value: form.key,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            key: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Description"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    value: form.description,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            description: e.target.value
                                        })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            disabled: !valid || create.isPending,
                            onClick: async ()=>{
                                await create.mutateAsync(form);
                                setForm({
                                    displayName: '',
                                    key: '',
                                    description: ''
                                });
                                onOpenChange(false);
                            },
                            children: "Create"
                        })
                    ]
                })
            ]
        })
    });
}
function EditGroupDialog({ group, open, onOpenChange }) {
    const [form, setForm] = useState({
        displayName: group.displayName,
        description: group.description
    });
    const update = useUpdateGroup();
    return /*#__PURE__*/ _jsx(Dialog, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(DialogContent, {
            "data-testid": Testids.adminGroups.editDialog,
            children: [
                /*#__PURE__*/ _jsx(DialogHeader, {
                    children: /*#__PURE__*/ _jsx(DialogTitle, {
                        children: "Edit group"
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "space-y-4",
                    children: [
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Display name"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    value: form.displayName,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            displayName: e.target.value
                                        })
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Key"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    value: group.key,
                                    disabled: true
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("div", {
                            className: "space-y-1.5",
                            children: [
                                /*#__PURE__*/ _jsx(Label, {
                                    children: "Description"
                                }),
                                /*#__PURE__*/ _jsx(Textarea, {
                                    value: form.description,
                                    onChange: (e)=>setForm({
                                            ...form,
                                            description: e.target.value
                                        })
                                })
                            ]
                        })
                    ]
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "flex justify-end gap-2",
                    children: [
                        /*#__PURE__*/ _jsx(Button, {
                            variant: "outline",
                            onClick: ()=>onOpenChange(false),
                            children: "Cancel"
                        }),
                        /*#__PURE__*/ _jsx(Button, {
                            disabled: !form.displayName || update.isPending,
                            onClick: async ()=>{
                                await update.mutateAsync({
                                    id: group.id,
                                    ...form
                                });
                                onOpenChange(false);
                            },
                            children: "Save"
                        })
                    ]
                })
            ]
        })
    });
}
function GroupDetailDrawer({ group, open, onOpenChange, onGrant }) {
    const [memberQuery, setMemberQuery] = useState('');
    const { data: members } = useGroupMembers(group.id, open);
    const { data: assignments } = useGroupAssignments(group.id, open);
    const { data: bundles } = useListBundles(true);
    const bundleByKey = new Map((bundles ?? []).map((b)=>[
            b.key,
            b
        ]));
    const { data: userSearch } = useListUsers(memberQuery, 1, 8);
    const names = useAdminNames((members ?? []).map((m)=>m.userId));
    const addMember = useAddGroupMember();
    const removeMember = useRemoveGroupMember();
    const memberIds = new Set((members ?? []).map((m)=>m.userId));
    return /*#__PURE__*/ _jsx(Sheet, {
        open: open,
        onOpenChange: onOpenChange,
        children: /*#__PURE__*/ _jsxs(SheetContent, {
            side: "right",
            className: "flex w-full flex-col overflow-y-auto sm:max-w-md",
            "data-testid": Testids.adminGroups.detailDrawer,
            children: [
                /*#__PURE__*/ _jsx(SheetHeader, {
                    children: /*#__PURE__*/ _jsx(SheetTitle, {
                        children: group.displayName
                    })
                }),
                /*#__PURE__*/ _jsxs("div", {
                    className: "mt-4 space-y-6 text-sm",
                    children: [
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsx("div", {
                                    className: "text-muted-foreground",
                                    children: "Add member"
                                }),
                                /*#__PURE__*/ _jsx(Input, {
                                    "data-testid": Testids.adminGroups.addMemberInput,
                                    placeholder: "Search people by name or email",
                                    value: memberQuery,
                                    onChange: (e)=>setMemberQuery(e.target.value)
                                }),
                                memberQuery && /*#__PURE__*/ _jsx("div", {
                                    className: "max-h-40 overflow-auto rounded-md border",
                                    children: (userSearch?.users ?? []).filter((u)=>!memberIds.has(u.id)).map((u)=>/*#__PURE__*/ _jsxs("button", {
                                            type: "button",
                                            "data-testid": Testids.adminGroups.memberOption,
                                            "data-user-id": u.id,
                                            className: "block w-full px-3 py-2 text-left hover:bg-muted",
                                            onClick: ()=>{
                                                addMember.mutate({
                                                    groupId: group.id,
                                                    userId: u.id
                                                });
                                                setMemberQuery('');
                                            },
                                            children: [
                                                /*#__PURE__*/ _jsx("div", {
                                                    children: u.name || u.email
                                                }),
                                                /*#__PURE__*/ _jsx("div", {
                                                    className: "text-xs text-muted-foreground",
                                                    children: u.email
                                                })
                                            ]
                                        }, u.id))
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "text-muted-foreground",
                                    children: [
                                        "Members (",
                                        (members ?? []).length,
                                        ")"
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        (members ?? []).map((m)=>/*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center justify-between rounded-md border px-3 py-1.5",
                                                children: [
                                                    /*#__PURE__*/ _jsx(ResolvedId, {
                                                        id: m.userId,
                                                        name: names.user(m.userId)
                                                    }),
                                                    /*#__PURE__*/ _jsx(Button, {
                                                        variant: "ghost",
                                                        size: "icon",
                                                        onClick: ()=>removeMember.mutate({
                                                                groupId: group.id,
                                                                userId: m.userId
                                                            }),
                                                        children: /*#__PURE__*/ _jsx(X, {
                                                            className: "h-4 w-4"
                                                        })
                                                    })
                                                ]
                                            }, m.id)),
                                        (members ?? []).length === 0 && /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "No members yet"
                                        })
                                    ]
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsxs("section", {
                            className: "space-y-2",
                            children: [
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between",
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-muted-foreground",
                                            children: "Group access"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            size: "sm",
                                            variant: "outline",
                                            "data-testid": Testids.adminGroups.grantButton,
                                            onClick: onGrant,
                                            children: "Grant access"
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs("div", {
                                    className: "space-y-1",
                                    children: [
                                        (assignments ?? []).map((a)=>{
                                            const bundle = bundleByKey.get(a.roleBundleKey);
                                            return /*#__PURE__*/ _jsxs("div", {
                                                className: "rounded-md border px-3 py-2",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("div", {
                                                        className: "flex items-center justify-between gap-2",
                                                        children: [
                                                            /*#__PURE__*/ _jsx("span", {
                                                                className: "font-medium",
                                                                children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                                    id: a.roleBundleKey,
                                                                    name: names.bundle(a.roleBundleKey)
                                                                })
                                                            }),
                                                            bundle && /*#__PURE__*/ _jsx(ViewModeBadge, {
                                                                mode: bundle.defaultViewMode
                                                            })
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsx("div", {
                                                        className: "text-xs text-muted-foreground",
                                                        children: /*#__PURE__*/ _jsx(ResolvedId, {
                                                            id: a.scopeNodeId,
                                                            name: names.scope(a.scopeNodeId)
                                                        })
                                                    }),
                                                    bundle && bundle.permittedActions.length > 0 && /*#__PURE__*/ _jsx("div", {
                                                        className: "mt-1 flex flex-wrap gap-1",
                                                        children: bundle.permittedActions.map((act)=>/*#__PURE__*/ _jsx("span", {
                                                                className: "rounded bg-muted px-1.5 py-0.5 text-[10px]",
                                                                children: actionLabel(act)
                                                            }, act))
                                                    })
                                                ]
                                            }, a.id);
                                        }),
                                        (assignments ?? []).length === 0 && /*#__PURE__*/ _jsx("span", {
                                            className: "text-muted-foreground",
                                            children: "No grants yet"
                                        })
                                    ]
                                }),
                                (assignments ?? []).length > 0 && /*#__PURE__*/ _jsxs("div", {
                                    className: "rounded-md border bg-muted/20 px-3 py-2",
                                    "data-testid": Testids.adminConsole.groupEffectivePermissions,
                                    children: [
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "text-xs font-medium uppercase tracking-wide text-muted-foreground",
                                            children: "Members of this group can"
                                        }),
                                        /*#__PURE__*/ _jsx("div", {
                                            className: "mt-1 flex flex-wrap gap-1",
                                            children: [
                                                ...new Set((assignments ?? []).flatMap((a)=>bundleByKey.get(a.roleBundleKey)?.permittedActions ?? []))
                                            ].sort().map((act)=>/*#__PURE__*/ _jsx("span", {
                                                    className: "rounded bg-background px-1.5 py-0.5 text-[10px] ring-1 ring-border",
                                                    children: actionLabel(act)
                                                }, act))
                                        }),
                                        /*#__PURE__*/ _jsx("p", {
                                            className: "mt-1 text-[11px] text-muted-foreground",
                                            children: "Each action applies within its grant's scope shown above — see the Permissions tab for what every bundle means."
                                        })
                                    ]
                                })
                            ]
                        })
                    ]
                })
            ]
        })
    });
}
